import React, { useState, useEffect } from 'react';
import ManagementTabs from './components/ManagementTabs';
import ManagementTimeline from './components/ManagementTimeline';
import EmptyState from './components/EmptyState';
import { EyeIcon } from '@heroicons/react/20/solid';
import ExpedientSummary from '../../../../components/ExpedientSummary/ExpedientSummary';
import { expedientStatusLabel } from '@claimup/shared/constants/expedient';

const ManagementPanel = ({ expedient }) => {
  const [activeTab, setActiveTab] = useState('all');

  // Function to determine tab based on interaction type
  const getTabForInteractionType = interactionType => {
    if (!interactionType) return 'all';

    if (interactionType === 'whatsapp_sent' || interactionType === 'whatsapp_response') {
      return 'whatsapp';
    }
    if (
      interactionType === 'email_sent' ||
      interactionType === 'email_opened' ||
      interactionType === 'email_response'
    ) {
      return 'email';
    }
    if (interactionType?.includes('call')) {
      return 'call';
    }
    if (interactionType === 'sms_sent' || interactionType === 'sms_opened' || interactionType === 'sms_responded') {
      return 'sms';
    }
    return 'all';
  };

  // Set tab based on last interaction when expedient changes
  useEffect(() => {
    if (!expedient || !expedient.interactions || expedient.interactions.length === 0) {
      setActiveTab('all');
      return;
    }

    // Find the most recent interaction (newest first)
    const sortedInteractions = [...expedient.interactions].sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateB - dateA; // Descending order: newest first
    });

    const lastInteraction = sortedInteractions[0];
    if (lastInteraction && lastInteraction.type) {
      const tabForLastInteraction = getTabForInteractionType(lastInteraction.type);
      setActiveTab(tabForLastInteraction);
    } else {
      setActiveTab('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expedient?.expedient_id]);

  if (!expedient) {
    return <EmptyState />;
  }

  const expedientId = expedient.expedient_id || expedient._id;
  const debtor = expedient.deudores && expedient.deudores.length > 0 ? expedient.deudores[0] : null;
  const debtorId = debtor ? debtor._id : null;
  const interactions = expedient.interactions || [];
  const annotations = expedient.annotations || [];

  const handleAnnotationAdded = newAnnotation => {
    // This will trigger a refresh of the expedient data
    // The parent component should handle refreshing the expedient list
  };

  return (
    <div className='flex-1 flex flex-col bg-white border border-slate-200 rounded-lg overflow-hidden shadow-lg'>
      {/* Header */}
      <div className='px-6 py-4 border-b border-slate-200 bg-slate-50'>
        <div className='flex items-center'>
          <h2 className='text-lg font-semibold text-slate-900'>
            {expedient.deudores && expedient.deudores.length > 0 ? expedient.deudores[0].debtorname : 'Sin deudor'}
          </h2>
          <p className='text-sm text-slate-500 ml-4'>{interactions.length} interacciones</p>

          <div className='ml-auto inline-flex items-center gap-3'>
            <div className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800'>
              {expedientStatusLabel(expedient.estado)}
            </div>

            <button
              onClick={() => window.open(`/view/${expedient.expedient_id}`, '_blank')}
              className='text-blue-500 hover:text-blue-600 transition-colors'
              title='Editar expediente'
            >
              <EyeIcon className='h-4 w-4' />
            </button>
          </div>
        </div>
      </div>

      {/* Resumen IA — encima de las pestañas, en la misma columna que resume:
          es lo primero que se ve al seleccionar un expediente. */}
      <ExpedientSummary key={expedientId} expedientId={expedientId} />

      {/* Tabs */}
      <ManagementTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Management Content */}
      <div className='min-h-[calc(100vh-315px)] max-h-[calc(100vh-315px)] '>
        <ManagementTimeline
          interactions={interactions}
          expedientId={expedientId}
          debtorId={debtorId}
          debtor={debtor}
          activeTab={activeTab}
          annotations={annotations}
          onAnnotationAdded={handleAnnotationAdded}
          expedientType={expedient.type}
        />
      </div>
    </div>
  );
};

export default ManagementPanel;
